/** @jsx jsx */

import type {
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { jsx } from '@balance-web/core';
import { makeId, useId } from '@balance-web/utils';
import { Toggle } from '@balance-web/toggle';

import { DragHandle, Group, MenuItem } from './styled-components';
import type { ConfigMenuGroupProps } from './types';

export const ConfigMenuGroup = <Value extends string | number>({
  onChange,
  onReorder,
  options,
  title,
  value,
}: ConfigMenuGroupProps<Value>) => {
  const groupId = useId();

  function onDragEnd(result: DropResult) {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    // sort the items
    const reorderedOptions = Array.from(options);
    const [removed] = reorderedOptions.splice(result.source.index, 1);
    reorderedOptions.splice(result.destination.index, 0, removed);

    if (onReorder) {
      onReorder(reorderedOptions);
    }
  }

  // help consumers handle multi value change
  function handleChange(selected: Value) {
    if (value.includes(selected)) {
      onChange(
        value.filter((v) => {
          return v !== selected;
        })
      );
    } else {
      onChange(value.concat(selected));
    }
  }

  // temp hack until we upgrade to React v18, which exports `React.useId()`
  // @see https://reactjs.org/docs/hooks-reference.html#useid
  const droppableId = groupId || 'group-id';

  // Fork render to support drag-n-drop
  if (onReorder) {
    return (
      <Group id={groupId} title={title}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId={droppableId}
            renderClone={renderCloneFromProps({ options, value })}
          >
            {(droppableProvided) => {
              return (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {options.map((item, index) => {
                    const checked = value.includes(item.value);
                    const id = item.value.toString();
                    const labelId = makeId(groupId, id);

                    return (
                      <Draggable
                        key={id}
                        draggableId={id}
                        index={index}
                        isDragDisabled={item.reOrderDisabled}
                      >
                        {(draggableProvided) => {
                          return (
                            <MenuItem
                              {...draggableProvided.draggableProps}
                              ref={draggableProvided.innerRef}
                              afterElement={
                                <Toggle
                                  aria-labelledby={labelId}
                                  checked={checked}
                                  disabled={item.toggleDisabled}
                                  size="small"
                                  onChange={() => {
                                    return handleChange(item.value);
                                  }}
                                />
                              }
                              beforeElement={
                                <DragHandle
                                  aria-label={`${item.label} drag handle`}
                                  disabled={item.reOrderDisabled}
                                  {...draggableProvided.dragHandleProps}
                                />
                              }
                              disabled={
                                item.toggleDisabled && item.reOrderDisabled
                              }
                            >
                              <span id={labelId}>{item.label}</span>
                            </MenuItem>
                          );
                        }}
                      </Draggable>
                    );
                  })}

                  {droppableProvided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
      </Group>
    );
  }

  // Standard items with toggle only
  return (
    <Group id={groupId} title={title}>
      {options.map((item) => {
        const checked = value.includes(item.value);
        const id = item.value.toString();
        const labelId = makeId(groupId, id);

        return (
          <MenuItem
            key={id}
            afterElement={
              <Toggle
                aria-labelledby={labelId}
                checked={checked}
                disabled={item.toggleDisabled}
                size="small"
                onChange={() => {
                  return handleChange(item.value);
                }}
              />
            }
            disabled={item.toggleDisabled}
          >
            <span id={labelId}>{item.label}</span>
          </MenuItem>
        );
      })}
    </Group>
  );
};

// Utils
// ------------------------------

type RenderCloneOptions<Value> = Pick<
  ConfigMenuGroupProps<Value>,
  'options' | 'value'
>;

/**
 * Create a "clone" of the menu item for react-beautiful-dnd. Necessary due to
 * the transform/translate positioning of popper.js dialog
 */
function renderCloneFromProps<Value>({
  options,
  value,
}: RenderCloneOptions<Value>) {
  return (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric
  ) => {
    const item = options[rubric.source.index];
    const checked = value.includes(item.value);

    return (
      <MenuItem
        {...provided.draggableProps}
        ref={provided.innerRef}
        afterElement={
          <Toggle
            checked={checked}
            disabled={item.toggleDisabled}
            size="small"
          />
        }
        beforeElement={
          <DragHandle
            aria-label={`${item.label} drag handle`}
            disabled={item.reOrderDisabled}
            {...provided.dragHandleProps}
          />
        }
        disabled={item.toggleDisabled && item.reOrderDisabled}
      >
        {item.label}
      </MenuItem>
    );
  };
}
